import React from 'react'
import { useAtomValue } from 'jotai'
import { startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, RowsSkeletonLoader } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { isContactEntityCompany } from 'pages/Contacts/utils/utils'
import { BeneficiaryTransferDestination, PaymentMethods } from 'types/beneficiaries'
import { ContactInstrument } from 'types/contacts'
import { getAccountNumberAndBankCodeLabelKeys, getAddress } from 'utils/beneficiaries'
import { getCountryNameByCode, sentenceCase } from 'utils/utils'

interface BankAccountSidePanelBodyProps {
  contactInstrument: ContactInstrument
}

export const BankAccountSidePanelBody = ({ contactInstrument }: BankAccountSidePanelBodyProps) => {
  const { t } = useTranslation()

  const { data: countries, isPending: isLoadingCountries } = useCountriesQuery(true)

  const contact = useAtomValue(contactAtom)

  const {
    accountNumber,
    accountType,
    currency,
    bank,
    paymentMethods,
    intermediaryBanks,
    accountHolderAddress,
    accountHolderName,
  } = contactInstrument || {}

  // Extract bank details from nested object
  const {
    name: bankName,
    identificationCode: routingNumber,
    swiftCode,
    address: bankAddress,
  } = bank || {}

  const isInternational = paymentMethods?.includes(PaymentMethods.SWIFT)
  const transferDestination = isInternational
    ? BeneficiaryTransferDestination.INTERNATIONAL
    : BeneficiaryTransferDestination.LOCAL
  const accountNumberAndBankCodeLabelKeys = getAccountNumberAndBankCodeLabelKeys(
    currency as any,
    transferDestination
  )

  const { country: bankAddressCountry } = bankAddress || {}
  const bankAddressFormatted = bankAddress
    ? getAddress(
        {
          ...bankAddress,
          addressLine2: bankAddress?.addressLine2 || '',
          region: bankAddress?.region || '',
        },
        getCountryNameByCode(bankAddressCountry, countries)
      )
    : null

  const { country: accountHolderAddressCountry } = accountHolderAddress || {}
  const accountHolderAddressFormatted = accountHolderAddress
    ? getAddress(
        {
          ...accountHolderAddress,
          addressLine2: accountHolderAddress?.addressLine2 || '',
          region: accountHolderAddress?.region || '',
        },
        getCountryNameByCode(accountHolderAddressCountry, countries)
      )
    : null

  const intermediaryBank = intermediaryBanks?.[0]
  const accountHolderFullName = accountHolderName?.legalName
    ? accountHolderName.legalName
    : [accountHolderName?.firstName, accountHolderName?.lastName].filter(Boolean).join(' ') || null
  const contactEntity = contact?.entity
  const isCompany = contactEntity && isContactEntityCompany(contactEntity)

  const contactAddress = contactInstrument?.accountHolderAddress
  const { country: contactAddressCountry } = contactAddress || {}
  const contactAddressFormatted = contactAddress
    ? getAddress(
        {
          ...contactAddress,
          addressLine2: contactAddress?.addressLine2 || '',
          region: contactAddress?.region || '',
        },
        getCountryNameByCode(contactAddressCountry, countries)
      )
    : null

  const isAccountHolderDataPresent =
    !!accountHolderFullName ||
    !!accountHolderAddressFormatted ||
    !!contactAddressFormatted ||
    !!contactAddressCountry

  const intermediaryBankAddress = intermediaryBank?.address
  const {
    addressLine1: intermediaryBankAddressLine1,
    city: intermediaryBankCity,
    postCode: intermediaryBankPostCode,
    region: intermediaryBankRegion,
    country: intermediaryBankCountry,
  } = intermediaryBankAddress || {}
  const hasIntermediaryBankAddress =
    intermediaryBankAddressLine1 || intermediaryBankCity || intermediaryBankCountry
  const isIntermediaryBankDataPresent =
    intermediaryBank?.identificationCode || intermediaryBank?.name
  const hasBeneficiaryAddress =
    !accountHolderAddressFormatted && !contactAddressFormatted && contactAddressCountry
  const hasAddress = !accountHolderAddressFormatted && !!contactAddressFormatted

  return isLoadingCountries ? (
    <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  ) : (
    <Box>
      {isAccountHolderDataPresent && (
        <Box>
          <h4 className="text-foreground-prominent text-h4">
            {t('contacts.accountHolderDetails')}
          </h4>
          <Box paddingT={8} paddingB={12}>
            {accountHolderFullName && (
              <DataListItem
                id="account-holder-name"
                testId="account-holder-name"
                title={t('accountHolder')}
                body={accountHolderFullName}
              />
            )}
            {!!accountHolderAddressFormatted && (
              <DataListItem
                id="account-holder-address"
                testId="account-holder-address"
                title={t('manageBeneficiaries.beneficiaryAddress')}
                body={accountHolderAddressFormatted}
              />
            )}
            {hasAddress && (
              <DataListItem
                id="contact-address"
                testId="contact-address"
                title={t('manageBeneficiaries.beneficiaryAddress')}
                body={contactAddressFormatted}
              />
            )}
            {hasBeneficiaryAddress && (
              <DataListItem
                id="contact-address-country"
                testId="contact-address-country"
                title={
                  isCompany
                    ? t('manageBeneficiaries.countryOfRegistration')
                    : t('manageBeneficiaries.countryOfResidence')
                }
                body={getCountryNameByCode(contactAddressCountry, countries)}
              />
            )}
          </Box>
        </Box>
      )}

      <h4 className="text-foreground-prominent text-h4">{t('bankDetails')}</h4>
      <Box paddingT={8} paddingB={12}>
        {accountNumber && (
          <DataListItem
            id="account-number"
            testId="account-number"
            title={t(accountNumberAndBankCodeLabelKeys?.accountNumberLabelKey || 'accountNumber')}
            body={ellipseMiddleText(accountNumber, 8, 8)}
            copyText={accountNumber}
          />
        )}
        {swiftCode && (
          <DataListItem
            id="bank-code"
            testId="bank-code"
            title={t(accountNumberAndBankCodeLabelKeys?.bankCodeLabelKey || 'bicSwiftCode')}
            body={swiftCode}
            copyText={swiftCode}
          />
        )}
        {routingNumber && (
          <DataListItem
            id="routing-number"
            testId="routing-number"
            title={t('abaRoutingNumber')}
            body={routingNumber}
            copyText={routingNumber}
          />
        )}
        {accountType && (
          <DataListItem
            id="account-type"
            testId="account-type"
            title={t('accountType')}
            body={sentenceCase(accountType?.toLowerCase())}
          />
        )}
        {bankName && (
          <DataListItem
            id="bank-name"
            testId="bank-name"
            title={t('bankName')}
            body={startCase(bankName?.toLowerCase())}
          />
        )}
        {!!bankAddressFormatted && (
          <DataListItem
            id="bank-address"
            testId="bank-address"
            title={t('bankAddress')}
            body={bankAddressFormatted}
          />
        )}
        {paymentMethods && !!paymentMethods.length && (
          <DataListItem
            id="payment-methods"
            testId="payment-methods"
            title={t(paymentMethods.length > 1 ? 'paymentMethods' : 'paymentMethod')}
            body={paymentMethods.join(', ')}
          />
        )}
      </Box>

      {isIntermediaryBankDataPresent && isInternational && (
        <Box>
          <h4 className="text-foreground-prominent text-h4">
            {t('manageBeneficiaries.intermediaryBankDetails')}
          </h4>
          <Box paddingT={8} paddingB={12}>
            {intermediaryBank?.swiftCode && (
              <DataListItem
                id="intermediary-bank-code"
                testId="intermediary-bank-code"
                title={t('bicSwiftCode')}
                body={intermediaryBank.swiftCode}
                copyText={intermediaryBank.swiftCode}
              />
            )}
            {intermediaryBank?.identificationCode && (
              <DataListItem
                id="intermediary-routing-number"
                testId="intermediary-routing-number"
                title={t('abaRoutingNumber')}
                body={intermediaryBank.identificationCode}
                copyText={intermediaryBank.identificationCode}
              />
            )}
            {intermediaryBank?.name && (
              <DataListItem
                id="intermediary-bank-name"
                testId="intermediary-bank-name"
                title={t('bankName')}
                body={intermediaryBank.name}
              />
            )}
            {hasIntermediaryBankAddress && (
              <DataListItem
                id="intermediary-bank-address"
                testId="intermediary-bank-address"
                title={t('bankAddress')}
                body={getAddress({
                  addressLine1: intermediaryBankAddressLine1,
                  city: intermediaryBankCity,
                  postCode: intermediaryBankPostCode,
                  region: intermediaryBankRegion,
                  country: getCountryNameByCode(intermediaryBankCountry, countries),
                })}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
